import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import V2Header from '../../components/v2/layout/V2Header';
import V2Container from '../../components/v2/layout/V2Container';
import V2Button from '../../components/v2/forms/V2Button';
import V2Input from '../../components/v2/forms/V2Input';
import V2Select from '../../components/v2/forms/V2Select';
import V2Badge from '../../components/v2/ui/V2Badge';
import '../../v2-theme.css';
import './RestaurantProfile.css';

/**
 * Restaurant Profile Page
 * Matches restaurant-profile.html exactly
 * Features: Profile editing, subscription management (Pay Per Post vs Unlimited)
 */
const RestaurantProfile = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  const [restaurantProfile, setRestaurantProfile] = useState(null);
  const [businessForm, setBusinessForm] = useState({
    businessName: '',
    establishmentType: '',
    address: '',
    address2: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    phone: '',
    licenseNumber: ''
  });
  const [contactForm, setContactForm] = useState({
    contactName: '',
    contactTitle: '',
    contactEmail: '',
    commPreferences: { email: false, sms: false }
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isUnlimited, setIsUnlimited] = useState(false);
  const [adminGrant, setAdminGrant] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const ESTABLISHMENT_TYPES = [
    { value: '', label: 'Select Type' },
    { value: 'restaurant', label: 'Restaurant' },
    { value: 'hotel', label: 'Hotel' },
    { value: 'catering', label: 'Catering' },
    { value: 'event-venue', label: 'Event Venue' },
    { value: 'bar', label: 'Bar' },
    { value: 'cafe', label: 'Café' },
    { value: 'other', label: 'Other' }
  ];

  // Fetch restaurant profile
  const fetchRestaurantProfile = async () => {
    try {
      const response = await api.get(`/profiles/restaurant/${user.id}`);
      const profile = response.data;
      setRestaurantProfile(profile);

      // Populate forms
      setBusinessForm({
        businessName: profile.business_name || '',
        establishmentType: profile.establishment_type || '',
        address: profile.address || '',
        address2: profile.address2 || '',
        city: profile.city || '',
        state: profile.state || '',
        postalCode: profile.postal_code || '',
        country: profile.country || '',
        phone: profile.phone || '',
        licenseNumber: profile.license_number || ''
      });

      setContactForm({
        contactName: profile.contact_name || '',
        contactTitle: profile.contact_title || '',
        contactEmail: profile.contact_email || user.email || '',
        commPreferences: profile.communication_preferences || { email: false, sms: false }
      });

      setIsUnlimited(profile.subscription_tier === 'unlimited');
      setAdminGrant(profile.admin_grant_unlimited || false);
    } catch (err) {
      console.error('Error fetching restaurant profile:', err);
      setError('Failed to load profile data');
    }
  };

  // Handle business form changes
  const handleBusinessChange = (name, value) => {
    setBusinessForm(prev => ({ ...prev, [name]: value }));
  };

  // Handle contact form changes
  const handleContactChange = (name, value) => {
    setContactForm(prev => ({ ...prev, [name]: value }));
  };

  const handleCommPrefChange = (pref) => {
    setContactForm(prev => ({
      ...prev,
      commPreferences: {
        ...prev.commPreferences,
        [pref]: !prev.commPreferences[pref]
      }
    }));
  };

  // Handle password form changes
  const handlePasswordChange = (name, value) => {
    setPasswordForm(prev => ({ ...prev, [name]: value }));
  };

  // Check if US-based
  const isUSBased = (country) => {
    const normalized = country.trim().toLowerCase();
    return ['united states', 'usa', 'us', 'united states of america'].includes(normalized);
  };

  // Save business information
  const handleBusinessSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation: US-based restaurants require license number
    if (isUSBased(businessForm.country) && !businessForm.licenseNumber.trim()) {
      alert('Food Service License Number is required for US-based restaurants');
      return;
    }

    setLoading(true);
    try {
      await api.put(`/profiles/restaurant/${user.id}`, {
        business_name: businessForm.businessName,
        establishment_type: businessForm.establishmentType,
        address: businessForm.address,
        address2: businessForm.address2,
        city: businessForm.city,
        state: businessForm.state,
        postal_code: businessForm.postalCode,
        country: businessForm.country,
        phone: businessForm.phone,
        license_number: businessForm.licenseNumber
      });

      alert('Business information updated successfully!');
    } catch (err) {
      console.error('Error updating business info:', err);
      setError(err.response?.data?.error || 'Failed to update business information');
    } finally {
      setLoading(false);
    }
  };

  // Save contact information
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!contactForm.commPreferences.sms) {
      alert('Text messaging is required to accept shifts');
      return;
    }

    setLoading(true);
    try {
      await api.put(`/profiles/restaurant/${user.id}`, {
        contact_name: contactForm.contactName,
        contact_title: contactForm.contactTitle,
        contact_email: contactForm.contactEmail,
        communication_preferences: contactForm.commPreferences
      });

      alert('Contact information updated successfully!');
    } catch (err) {
      console.error('Error updating contact info:', err);
      setError(err.response?.data?.error || 'Failed to update contact information');
    } finally {
      setLoading(false);
    }
  };

  // Update password
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (passwordForm.newPassword.length < 8) {
      alert('Password must be at least 8 characters long');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/change-password', {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      });

      alert('Password updated successfully!');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      console.error('Error updating password:', err);
      alert(err.response?.data?.error || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  // Logo upload
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Logo file size must be less than 5MB');
      e.target.value = '';
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', 'logo');

    try {
      setLoading(true);
      const response = await api.post('/profiles/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      await api.put(`/profiles/restaurant/${user.id}`, {
        business_logo: response.data.url
      });

      alert('Logo uploaded successfully!');
      await fetchRestaurantProfile();
    } catch (err) {
      console.error('Error uploading logo:', err);
      alert('Failed to upload logo');
    } finally {
      setLoading(false);
    }
  };

  // Upgrade to unlimited
  const handleUpgrade = () => {
    navigate('/checkout/restaurant-unlimited');
  };

  // Admin override toggle
  const handleAdminOverride = async () => {
    const newGrant = !adminGrant;
    try {
      await api.post(`/admin/grant-unlimited/${user.id}`, {
        grant: newGrant
      });
      setAdminGrant(newGrant);
      alert(newGrant
        ? 'Unlimited features granted to this Pay Per Post account.\n\nThis override is for promotional/demo purposes only.'
        : 'Unlimited features removed. Account reverted to standard Pay Per Post billing.');
      await fetchRestaurantProfile();
    } catch (err) {
      console.error('Error toggling admin override:', err);
      alert('Failed to update admin override');
    }
  };

  // Initial data fetch
  useEffect(() => {
    if (user) {
      fetchRestaurantProfile();
    }
  }, [user]);

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <div className="restaurant-profile-page">
      <V2Header
        title="Hands On The Fly"
        businessName={restaurantProfile?.business_name || 'Restaurant'}
        userType="restaurant"
        showProfileLink={false}
        onLogout={handleLogout}
      >
        <Link to="/dashboard/restaurant" className="nav-link-header">Dashboard</Link>
      </V2Header>

      <V2Container maxWidth="md">
        <div className="page-header">
          <h2 className="v2-heading-xl">Restaurant Profile</h2>
          <p className="v2-text-silver">Manage your account settings</p>
        </div>

        {error && <div className="v2-error-message">{error}</div>}

        {/* Business Information */}
        <div className="profile-section">
          <h3 className="profile-section-title">Business Information</h3>

          <form onSubmit={handleBusinessSubmit}>
            <div className="logo-upload-section">
              <label>Business Logo</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                disabled={loading}
                className="file-input"
              />
              <p className="feature-note">Upload your restaurant logo (JPG, PNG, max 5MB)</p>
            </div>

            <V2Input
              label="Business Name"
              value={businessForm.businessName}
              onChange={(e) => handleBusinessChange('businessName', e.target.value)}
              required
              disabled={loading}
            />

            <V2Select
              label="Establishment Type"
              value={businessForm.establishmentType}
              onChange={(e) => handleBusinessChange('establishmentType', e.target.value)}
              options={ESTABLISHMENT_TYPES}
              required
              disabled={loading}
            />

            <V2Input
              label="Street Address"
              value={businessForm.address}
              onChange={(e) => handleBusinessChange('address', e.target.value)}
              required
              disabled={loading}
            />

            <V2Input
              label="Street Address 2 (Optional)"
              value={businessForm.address2}
              onChange={(e) => handleBusinessChange('address2', e.target.value)}
              placeholder="Apt, Suite, Unit, Building, Floor, etc."
              disabled={loading}
            />

            <div className="form-row">
              <V2Input
                label="City"
                value={businessForm.city}
                onChange={(e) => handleBusinessChange('city', e.target.value)}
                required
                disabled={loading}
              />
              <V2Input
                label="State/Province/Region"
                value={businessForm.state}
                onChange={(e) => handleBusinessChange('state', e.target.value)}
                required
                disabled={loading}
              />
            </div>

            <div className="form-row">
              <V2Input
                label="Postal Code"
                value={businessForm.postalCode}
                onChange={(e) => handleBusinessChange('postalCode', e.target.value)}
                required
                disabled={loading}
              />
              <V2Input
                label="Country"
                value={businessForm.country}
                onChange={(e) => handleBusinessChange('country', e.target.value)}
                required
                disabled={loading}
              />
            </div>

            <V2Input
              label="Phone Number"
              type="tel"
              value={businessForm.phone}
              onChange={(e) => handleBusinessChange('phone', e.target.value)}
              required
              disabled={loading}
            />

            <V2Input
              label="Food Service License Number"
              value={businessForm.licenseNumber}
              onChange={(e) => handleBusinessChange('licenseNumber', e.target.value)}
              disabled={loading}
            />
            <p className="feature-note">Required for US-based restaurants</p>

            <V2Button type="submit" variant="steel" fullWidth disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </V2Button>
          </form>
        </div>

        {/* Contact Information */}
        <div className="profile-section">
          <h3 className="profile-section-title">Contact Information</h3>

          <form onSubmit={handleContactSubmit}>
            <V2Input
              label="Name"
              value={contactForm.contactName}
              onChange={(e) => handleContactChange('contactName', e.target.value)}
              required
              disabled={loading}
            />

            <V2Input
              label="Title"
              value={contactForm.contactTitle}
              onChange={(e) => handleContactChange('contactTitle', e.target.value)}
              required
              disabled={loading}
            />

            <V2Input
              label="Email"
              type="email"
              value={contactForm.contactEmail}
              onChange={(e) => handleContactChange('contactEmail', e.target.value)}
              required
              disabled={loading}
            />

            <div className="comm-pref-section">
              <p className="comm-pref-header">
                I agree to receive communications from Hands On The Fly via:
              </p>
              <div className="checkbox-group">
                <label className="checkbox-label-small">
                  <input
                    type="checkbox"
                    checked={contactForm.commPreferences.email}
                    onChange={() => handleCommPrefChange('email')}
                    disabled={loading}
                  />
                  <span>Email (Optional)</span>
                </label>
                <label className="checkbox-label-small">
                  <input
                    type="checkbox"
                    checked={contactForm.commPreferences.sms}
                    onChange={() => handleCommPrefChange('sms')}
                    disabled={loading}
                  />
                  <span>Text Message (Required)</span>
                </label>
              </div>
              <p className="feature-note">Text messaging required to accept shifts</p>
            </div>

            <V2Button type="submit" variant="steel" fullWidth disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </V2Button>
          </form>
        </div>

        {/* Change Password */}
        <div className="profile-section">
          <h3 className="profile-section-title">Change Password</h3>

          <form onSubmit={handlePasswordSubmit}>
            <V2Input
              label="Current Password"
              type="password"
              value={passwordForm.currentPassword}
              onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
              required
              disabled={loading}
            />

            <V2Input
              label="New Password"
              type="password"
              value={passwordForm.newPassword}
              onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
              required
              minLength="8"
              disabled={loading}
            />
            <p className="feature-note">Minimum 8 characters</p>

            <V2Input
              label="Confirm New Password"
              type="password"
              value={passwordForm.confirmPassword}
              onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
              required
              minLength="8"
              disabled={loading}
            />

            <V2Button type="submit" variant="steel" fullWidth disabled={loading}>
              {loading ? 'Updating...' : 'Update Password'}
            </V2Button>
          </form>
        </div>

        {/* Subscription */}
        <div className="profile-section">
          <h3 className="profile-section-title">Subscription</h3>

          <div className="subscription-status">
            <div>
              <div className="current-plan">
                Current Plan: <strong>{isUnlimited ? 'Unlimited' : 'Pay Per Post'}</strong>
              </div>
              <div className="plan-description">
                {isUnlimited ? 'Unlimited shift posts' : '$19 per shift posted'}
              </div>
            </div>
          </div>

          <div className="subscription-grid">
            {/* Pay Per Post Plan */}
            <div className="plan-card plan-card-current">
              <div className="plan-name">PAY PER POST</div>
              <div className="plan-price">$19<span className="plan-period">/shift</span></div>
              <ul className="plan-features">
                <li>✓ Post shifts as needed</li>
                <li>✓ Pay only when you post</li>
                <li>✓ Basic profile</li>
                <li>✓ Worker search</li>
              </ul>
              <button className="plan-btn-current" disabled>Current Plan</button>
            </div>

            {/* Unlimited Plan */}
            <div className="plan-card plan-card-unlimited">
              <div className="plan-name">UNLIMITED</div>
              <div className="plan-price">$99<span className="plan-period">/month</span></div>
              <div className="plan-yearly">
                or $999/year <span className="yearly-savings">(save $189)</span>
              </div>
              <ul className="plan-features">
                <li>✓ Unlimited shift posts</li>
                <li>✓ Enhanced profile</li>
                <li>✓ Priority listing</li>
                <li>✓ Advanced analytics</li>
              </ul>
              <V2Button variant="steel" fullWidth onClick={handleUpgrade}>
                Upgrade
              </V2Button>
            </div>
          </div>

          {/* Admin Override (for admin users only) */}
          {user?.user_type === 'admin' && (
            <div className="admin-override-box">
              <div className="admin-override-header">⚠ Admin Controls</div>
              <div className="admin-override-description">
                Grant Unlimited features to this Pay Per Post account for promotions, demos, or special campaigns.
              </div>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={adminGrant}
                  onChange={handleAdminOverride}
                />
                <span>Grant Unlimited Features (Pay Per Post account remains)</span>
              </label>
              <V2Button
                variant="steel"
                fullWidth
                onClick={handleAdminOverride}
                className="admin-override-btn"
              >
                Save Admin Override
              </V2Button>
            </div>
          )}
        </div>
      </V2Container>
    </div>
  );
};

export default RestaurantProfile;
